import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import PageShell from '../components/PageShell';
import Icon from '../components/Icon';
import PageIntro from '../components/PageIntro';
import ServiceGlyph from '../components/ServiceGlyph';
import LicenceFile from '../components/LicenceFile';
import PageSection from '../components/page/PageSection';
import Eyebrow from '../components/page/Eyebrow';
import StepNumber from '../components/page/StepNumber';
import InlineCta from '../components/page/InlineCta';
import FaqList from '../components/page/FaqList';
import { tie } from '../components/page/typography';
import { findService, serviceMeta, serviceGroups } from '../content/services';
import { articles } from '../content/blog';
import { SITE } from '../components/Seo';

/**
 * Сторінка послуги — один шаблон на шість напрямків.
 *
 * Порядок підпорядкований тому, з чим сюди приходять із пошуку: спершу «що
 * ви для мене зробите» і «чи це про мене», потім — чому це не формальність,
 * як іде робота і де зазвичай ламається. Раніше під банером стояли два
 * абзаци вступу, і до переліку послуги на телефоні було два з половиною
 * екрани, а єдина кнопка заявки — аж у кінці, за дванадцять екранів.
 *
 * Заклики тепер три, і всі різні: одразу після «Кому це потрібно» —
 * загальний («уже бачите, що це ваш випадок?»), після етапів чи типових
 * помилок — конкретна пропозиція цієї послуги (meta.cta), у кінці —
 * банер PageShell. Один і той самий текст тричі на сторінці читався б як
 * реклама, тож meta.cta стоїть там, де до нього підводить зміст: «більшість
 * відмов видно ще до подання» — одразу під переліком цих відмов.
 */

// Вкладена підкладка в картці Scope. На телефоні друге поле всередині
// першого стискало текст до ~250px — по три слова в рядку, тож нижче sm
// підкладки немає: її місце займає риска, як між пунктами переліку.
const inset = 'border-t border-slate-200 pt-5 sm:border-0 sm:rounded-xl sm:bg-slate-50';

// «Результат: …», «Перевага: …» — мітку виділяємо, щоб рядок читався як
// підсумок переліку, а не як ще один пункт.
const Summary = ({ text }) => {
  const match = text.match(/^([^:]{2,20}):\s+([\s\S]*)$/);
  return (
    <div className={`mt-8 flex items-start gap-3 ${inset} sm:px-5 sm:py-4`}>
      <Icon name="fa-circle-check" className="mt-1 flex-shrink-0 text-teal-700" />
      <p className="text-base leading-relaxed text-slate-700">
        {match ? (
          <>
            <strong className="font-semibold text-slate-900">{match[1]}:</strong> {tie(match[2])}
          </>
        ) : (
          tie(text)
        )}
      </p>
    </div>
  );
};

const CheckList = ({ items, className = '' }) => (
  <ul className={`list-none m-0 p-0 grid gap-y-3 ${className}`}>
    {items.map((entry) => (
      <li key={entry} className="flex items-start gap-3">
        <Icon name="fa-check" className="mt-1.5 flex-shrink-0 text-sm text-teal-700" />
        <span className="leading-relaxed text-slate-700">{tie(entry)}</span>
      </li>
    ))}
  </ul>
);

// Скільки пунктів переліку в ряд: два-три — в один ряд, чотири — два на
// два. Чотири в ряд давали колонки по три слова, а пункт на 380 знаків
// у сусідстві з пунктом на 100 розтягувався на пів екрана вниз. Три в ряд —
// лише від lg: на планшеті колонка виходила на два слова в рядку.
const scopeColumns = (n) => {
  if (n === 3) return 'lg:grid-cols-3';
  if (n >= 2) return 'md:grid-cols-2';
  return '';
};

/**
 * Що входить у послугу — першим під банером: заради цього сюди й приходять.
 * Картка на всю ширину, пункти — рядом колонок. Поруч «Кому це потрібно»
 * ставити не можна: у переліку від двох до чотирьох пунктів різної
 * довжини, і сусідня колонка виходила то на третину нижчою, то вищою.
 *
 * Заголовок складено вручну, а не через SectionTitle: поруч стоїть знак
 * напрямку, той самий, що в меню, — по ньому видно, на якій із шести
 * послуг людина опинилася. Підпис і h2 — ті самі, що в кожній секції.
 */
const Scope = ({ index, items, footer, coverage }) => (
  <section aria-labelledby="scope-title" className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7 lg:p-10">
    <div className="flex items-center gap-4 lg:gap-5">
      <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center" aria-hidden="true">
        <ServiceGlyph index={index} className="w-7 h-7" />
      </span>
      <div className="min-w-0">
        <Eyebrow className="mb-3">Обсяг послуги</Eyebrow>
        <h2 id="scope-title" className="text-2xl lg:text-3xl font-extrabold font-display text-slate-900 leading-tight text-balance">
          Що входить у послугу
        </h2>
      </div>
    </div>

    <ul className={`list-none m-0 p-0 mt-7 grid gap-x-10 gap-y-6 ${scopeColumns(items.length)}`}>
      {items.map((item) => (
        <li key={item.label} className="border-t border-slate-200 pt-4">
          <h3 className="text-lg font-semibold font-display text-slate-900 leading-snug">{item.label}</h3>
          <p className="mt-1.5 leading-relaxed text-slate-600">{tie(item.text)}</p>
        </li>
      ))}
    </ul>

    {/*
      Розгорнутий перелік напрямків аутсорсингу. Тут же, у картці: пункт
      «Тематика супроводу» відсилає саме сюди («повний перелік — нижче»).
      Дев'ять пунктів — три рівні стовпці.
    */}
    {coverage && (
      <div className={`mt-8 ${inset} sm:p-6 lg:p-7`}>
        <h3 className="text-lg font-semibold font-display text-slate-900 leading-snug mb-4">{coverage.title}</h3>
        <CheckList items={coverage.items} className="gap-x-10 gap-y-3 md:grid-cols-2 lg:grid-cols-3" />
      </div>
    )}

    {footer && <Summary text={footer} />}
  </section>
);

/**
 * Кому це потрібно. Примітка з content (note) на сторінці ЕСОЗ — про те,
 * що реєстрація обов'язкова для всіх закладів, тож вона закриває саме цей
 * перелік. Раніше вона висіла окремою цитатою під вступом на пів ширини.
 */
const Audience = ({ items, note }) => (
  <PageSection
    id="komu"
    caption="Чи це про вас"
    title="Кому це потрібно"
    spacing="mt-6 lg:mt-8"
    className="rounded-2xl border border-slate-100 bg-slate-50 p-5 sm:p-7 lg:p-10"
  >
    <CheckList items={items} className="gap-x-10 md:grid-cols-2" />
    {note && (
      <div className="mt-6 pt-5 border-t border-slate-200">
        <p className="max-w-4xl leading-relaxed text-slate-600">{tie(note)}</p>
      </div>
    )}
  </PageSection>
);

/**
 * Етапи — шкалою: номер, назва, пояснення. Номери тут навмисно: людина, яка
 * вперше проходить процедуру, питає не «що ви робите», а «що буде далі», і
 * номер відповідає на це швидше за текст.
 *
 * Раніше етапи стояли картками 2×3 різної висоти: поруч із кроком на п'ять
 * рядків — крок на два, і пів картки порожні. Рядок на всю ширину не має
 * сусіда, з яким треба рівнятися. Від lg назва й пояснення — у дві колонки,
 * щоб шкала сканувалася за назвами, не вичитуючи абзаци.
 */
const Steps = ({ steps }) => (
  <ol className="list-none m-0 p-0">
    {steps.map((step, i) => {
      const last = i === steps.length - 1;
      return (
        <li
          key={step.label}
          className={`relative grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-4 sm:gap-x-5 lg:grid-cols-[2.5rem_minmax(0,17rem)_minmax(0,1fr)] lg:gap-x-10 ${last ? '' : 'pb-8 lg:pb-10'}`}
        >
          {!last && (
            <span className="absolute left-5 top-12 bottom-2 w-px bg-teal-200" aria-hidden="true"></span>
          )}
          <StepNumber n={i + 1} />
          <h3 className="pt-2 text-lg font-semibold font-display text-slate-900 leading-snug">{step.label}</h3>
          <p className="col-start-2 mt-1.5 lg:col-start-3 lg:row-start-1 lg:mt-0 lg:pt-2 max-w-2xl leading-relaxed text-slate-600">
            {tie(step.text)}
          </p>
        </li>
      );
    })}
  </ol>
);

/**
 * Типові помилки — єдиний блок у бурштиновому, як і попередження в статтях
 * блогу: це не ще один перелік робіт, а те, від чого послуга береже.
 * Підпис теж бурштиновий: бірюзовий над бурштиновою панеллю читався як
 * чужий.
 */
const Pitfalls = ({ items }) => (
  <PageSection
    id="pomylky"
    caption="Типові помилки"
    captionTone="amber"
    title="Через що це зазвичай зривається"
    className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-8 lg:p-10"
  >
    <ul className="list-none m-0 p-0 grid gap-x-10 gap-y-4 md:grid-cols-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-2.5 w-2 h-2 flex-shrink-0 rounded-full bg-amber-500" aria-hidden="true"></span>
          <span className="leading-relaxed text-slate-700">{tie(item)}</span>
        </li>
      ))}
    </ul>
  </PageSection>
);

/**
 * Інші напрямки — рядом плиток із тими самими малюнками, що в меню й на
 * сторінці всіх послуг: людина впізнає напрямок за знаком, а не вичитує.
 * П'ять плиток у два стовпці лишали порожню клітинку, тож від lg вони
 * стоять в один ряд, а нижче — списком на всю ширину.
 */
const OtherServices = ({ services }) => (
  <ul className="list-none m-0 p-0 grid gap-3 lg:grid-cols-5 lg:gap-4">
    {services.map((m) => (
      <li key={m.slug}>
        <Link
          to={`/poslugy/${m.slug}`}
          className="group flex h-full min-h-[3.25rem] items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 lg:flex-col lg:items-start lg:gap-4 lg:p-5 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
        >
          <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center" aria-hidden="true">
            <ServiceGlyph index={m.index} className="w-6 h-6" />
          </span>
          <span className="flex-1 min-w-0 font-semibold leading-snug text-slate-800 group-hover:text-teal-800 transition-colors duration-200">
            {tie(serviceGroups[m.index].title)}
          </span>
          <Icon name="fa-arrow-right" className="flex-shrink-0 text-sm text-teal-700" />
        </Link>
      </li>
    ))}
  </ul>
);

const ServicePage = () => {
  const { slug } = useParams();
  const service = findService(slug);

  // Невідомий slug — не показуємо порожню сторінку і не плодимо адрес,
  // за які Google потім чіплятиметься: просто відправляємо до переліку послуг.
  if (!service) return <Navigate to="/poslugy" replace />;

  const {
    meta, items, subtitle, note, specialties, footer, title, index,
    intro, audience, process, pitfalls, faq, coverage
  } = service;
  const others = serviceMeta
    .map((m, i) => ({ ...m, index: i }))
    .filter((m) => m.slug !== slug);
  // Статті блогу самі кажуть, до якої послуги вони належать (поле related),
  // тож нова стаття з'являється тут без правок у цьому файлі.
  const reading = articles.filter((a) => a.related === slug);

  // Примітка йде туди, до чого вона за змістом: на сторінці документів вона
  // пояснює перелік профілів, на сторінці ЕСОЗ — закриває «Кому це потрібно».
  const profilesNote = specialties ? note : null;
  const audienceNote = specialties ? null : note;

  // Назвою послуги в схемі стоїть h1, а не назва з меню: «Старт вашої справи
  // «під ключ»» нічого не каже пошуковику про те, що саме тут пропонують.
  const serviceSchema = {
    '@type': 'Service',
    name: meta.h1,
    alternateName: title,
    description: meta.description,
    serviceType: meta.h1,
    url: `${SITE}/poslugy/${slug}`,
    areaServed: { '@type': 'Country', name: 'Україна' },
    provider: { '@type': 'LegalService', name: 'Visia Medical Group', url: SITE },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `Що входить: ${meta.h1}`,
      itemListElement: items.map((item) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: item.label, description: item.text }
      }))
    }
  };

  // Питання й відповіді дублюються в розмітці для пошуковика рівно тим самим
  // текстом, що видно на сторінці: розходження між ними Google трактує як
  // спробу показати йому не те, що бачить людина.
  const faqSchema = faq && faq.length ? {
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  } : null;

  return (
    <PageShell
      title={meta.title}
      description={meta.description}
      path={`/poslugy/${slug}`}
      schema={faqSchema ? [serviceSchema, faqSchema] : [serviceSchema]}
      banner={meta.banner}
      cta={{ source: `service-${slug}-end` }}
      crumbs={[{ name: 'Послуги', to: '/poslugy' }, { name: title }]}
      heading={meta.h1}
      lead={subtitle}
    >
      <div className="page-container py-10 lg:py-14">
        <Scope index={index} items={items} footer={footer} coverage={coverage} />
        {audience && <Audience items={audience} note={audienceNote} />}

        <InlineCta source={`service-${slug}-top`} />

        {intro && (
          <PageSection id="sut" caption="Суть питання" title="Що важливо знати заздалегідь">
            <PageIntro paragraphs={intro} />
          </PageSection>
        )}

        {/*
          Спеціальності — плитками, а не рядком усередині абзацу: людина шукає
          в переліку свою і має знайти її поглядом.
        */}
        {specialties && (
          <PageSection
            id="profili"
            caption="Під ваш профіль"
            title="Профілі, під які готуємо документи"
            description={profilesNote}
          >
            <ul className="list-none m-0 p-0 flex flex-wrap gap-2.5">
              {specialties.map((name) => (
                <li
                  key={name}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[0.95rem] text-slate-700"
                >
                  {tie(name)}
                </li>
              ))}
              <li className="px-2 py-2 text-[0.95rem] text-slate-500">…та інші спеціальності.</li>
            </ul>
          </PageSection>
        )}

        {/* Склад ліцензійної справи — лише на сторінці ліцензування: саме
            тут людина читає, що подається до МОЗ, і тут малюнок на місці. */}
        {slug === 'licenzuvannya-medychnoyi-praktyky' && <LicenceFile />}

        {process && (
          <PageSection id="etapy" caption="Етапи" title="Як проходить робота">
            <Steps steps={process} />
          </PageSection>
        )}

        {pitfalls && <Pitfalls items={pitfalls} />}

        {meta.cta && (
          <InlineCta
            source={`service-${slug}-mid`}
            title={meta.cta.title}
            text={meta.cta.text}
          />
        )}

        {/*
          Відповіді розгорнуті, а не сховані під клік: людина з пошуку приходить
          саме за конкретним питанням, і текст під кліком вона не побачить, а
          Google вважає його менш вагомим за видимий. Перелік рядками, а не
          сіткою карток: картки різної висоти «рвали» сітку, а непарна
          кількість лишала порожню клітинку.
        */}
        {faq && (
          <PageSection id="pytannya" caption="Питання й відповіді" title="Часті запитання">
            <FaqList items={faq} openFirst questionAs="h3" />
          </PageSection>
        )}

        {/*
          Стоїть одразу під питаннями, бо саме там на сторінці ліцензування
          йдеться про Постанову № 781, а стаття розбирає її докладно.
        */}
        {reading.length > 0 && (
          <PageSection id="chytaty" caption="Блог" title="Читайте також">
            <ul className="list-none m-0 p-0 grid gap-4">
              {reading.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/blog/${a.slug}`}
                    className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 lg:p-6 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">{a.tag}</span>
                      <span className="mt-2 block text-lg font-semibold font-display leading-snug text-slate-900 group-hover:text-teal-800 transition-colors duration-200">
                        {tie(a.title)}
                      </span>
                      <span className="mt-1.5 block max-w-3xl leading-relaxed text-slate-600">{tie(a.excerpt)}</span>
                    </span>
                    <Icon
                      name="fa-arrow-right"
                      className="mt-1 flex-shrink-0 text-teal-700 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </PageSection>
        )}

        <PageSection
          id="inshi"
          caption="Інші послуги"
          title="Інші напрямки"
          link={{ to: '/poslugy', label: 'Усі послуги' }}
        >
          <OtherServices services={others} />
        </PageSection>
      </div>
    </PageShell>
  );
};

export default ServicePage;
